import React, { createContext, useContext, useState, useMemo, useEffect } from "react";
import { getBalance } from "@wagmi/core";
import { config } from "../../app/wagmi";
import { NATIVE, getChains, getChainById } from "common";
import { useAccount } from "wagmi";
import { zeroAddress } from "viem";
import { jsx as _jsx } from "react/jsx-runtime";
export const GITCOIN_RECIPIENT_CONFIG = {
  42220: {
    // Celo
    nonce: 10000n,
    recipient: "0x6a02e9bdAd1C5B8cBbC3B200F0aaE67496FFd4d4"
  },
  42161: {
    // Arbitrum One
    nonce: 10000n,
    recipient: "0x6a02e9bdAd1C5B8cBbC3B200F0aaE67496FFd4d4"
  },
  10: {
    // Optimism
    nonce: 10000n,
    recipient: "0x6a02e9bdAd1C5B8cBbC3B200F0aaE67496FFd4d4"
  },
  8453: {
    // Base
    nonce: 10000n,
    recipient: "0x6a02e9bdAd1C5B8cBbC3B200F0aaE67496FFd4d4"
  }
};
export const getGitcoinRecipientData = chainId => {
  const config = GITCOIN_RECIPIENT_CONFIG[chainId];
  if (!config) {
    throw new Error(`Unsupported chainId: ${chainId}`);
  }
  return config;
};
const DonateToGitcoinContext = /*#__PURE__*/createContext(null);
export function DonateToGitcoinProvider(_ref) {
  let {
    children
  } = _ref;
  const [selectedChainId, setSelectedChainId] = useState(null);
  const [isEnabled, setIsEnabled] = useState(false);
  const [selectedToken, setSelectedToken] = useState("");
  const [amount, setAmount] = useState("0.00");
  const [directAllocationPoolId, setDirectAllocationPoolId] = useState(null);
  const [tokenBalances, setTokenBalances] = useState({});
  const {
    address
  } = useAccount();
  const [tokenFilters, setTokenFilters] = useState(undefined);
  const [amountInWei, setAmountInWei] = useState(0n);
  const chains = useMemo(() => {
    const allChains = getChains().filter(c => c.type === "mainnet");
    if (!tokenFilters) return allChains;
    return allChains.filter(chain => tokenFilters.some(filter => filter.chainId === chain.id));
  }, [tokenFilters]);
  const selectedChain = selectedChainId ? chains.find(c => c.id === selectedChainId) || null : null;
  const selectedTokenBalance = useMemo(() => {
    var _tokenBalances$select;
    return selectedChainId && selectedToken ? ((_tokenBalances$select = tokenBalances[selectedChainId]) === null || _tokenBalances$select === void 0 ? void 0 : _tokenBalances$select[selectedToken]) || 0 : 0;
  }, [tokenBalances, selectedChainId, selectedToken]);
  useEffect(() => {
    if (!address || !tokenFilters) return;
    const fetchBalances = async () => {
      const balancesMap = {};

      // Process each chain's token filters
      await Promise.all(tokenFilters.map(async _ref2 => {
        let {
          chainId,
          addresses
        } = _ref2;
        const chain = getChainById(chainId);
        if (!chain) return;
        balancesMap[chainId] = {};

        // Fetch balances for filtered tokens
        const tokenBalances = await Promise.all(addresses.map(async tokenAddress => {
          const token = chain.tokens.find(t => t.address.toLowerCase() === tokenAddress.toLowerCase());
          if (!token) return null;
          const {
            value
          } = await getBalance(config, {
            address,
            token: tokenAddress.toLowerCase() === NATIVE.toLowerCase() || tokenAddress.toLowerCase() === zeroAddress.toLowerCase() ? undefined : tokenAddress.toLowerCase(),
            chainId
          });
          return {
            address: tokenAddress.toLowerCase() === NATIVE.toLowerCase() || tokenAddress.toLowerCase() === zeroAddress.toLowerCase() ? zeroAddress : tokenAddress,
            balance: Number(value) / 10 ** (token.decimals || 18)
          };
        }));

        // Add valid balances to the map
        tokenBalances.forEach(result => {
          if (result) {
            balancesMap[chainId][result.address] = result.balance;
          }
        });
      }));
      setTokenBalances(balancesMap);
    };
    fetchBalances();
  }, [address, tokenFilters]);
  useEffect(() => {
    if (tokenFilters) {
      const hasGitcoinSupportedChain = tokenFilters.some(filter => Object.keys(GITCOIN_RECIPIENT_CONFIG).includes(filter.chainId.toString()));
      setIsEnabled(hasGitcoinSupportedChain);
    }
  }, [tokenFilters]);
  useEffect(() => {
    if (!isEnabled) {
      setSelectedChainId(null);
      setSelectedToken("");
      setAmount("");
    }
  }, [isEnabled, setSelectedChainId, setSelectedToken, setAmount]);
  useEffect(() => {
    if (!selectedChainId) return;
    const fetchDirectAllocationPoolId = async () => {
      const poolId = getChainById(selectedChainId).contracts.directAllocationPoolId;
      setDirectAllocationPoolId(poolId !== null && poolId !== void 0 ? poolId : null);
    };
    fetchDirectAllocationPoolId();
  }, [selectedChainId]);
  const tokenDetails = selectedChain === null || selectedChain === void 0 ? void 0 : selectedChain.tokens.find(t => t.address === selectedToken);
  const filteredTokens = useMemo(() => {
    if (!selectedChain || !tokenFilters) return selectedChain === null || selectedChain === void 0 ? void 0 : selectedChain.tokens;
    const chainFilter = tokenFilters.find(f => f.chainId === selectedChain.id);
    if (!chainFilter) return selectedChain.tokens;
    return selectedChain.tokens.filter(token => chainFilter.addresses.map(addr => addr.toLowerCase()).includes(token.address.toLowerCase()));
  }, [selectedChain, tokenFilters]);
  const value = {
    isEnabled,
    selectedChainId,
    selectedToken,
    amount,
    directAllocationPoolId,
    tokenBalances,
    selectedTokenBalance,
    chains,
    selectedChain,
    tokenFilters,
    filteredTokens,
    tokenDetails,
    amountInWei,
    setTokenFilters,
    setIsEnabled,
    setSelectedChainId,
    setSelectedToken,
    setAmount,
    setAmountInWei
  };
  return /*#__PURE__*/_jsx(DonateToGitcoinContext.Provider, {
    value: value,
    children: children
  });
}
export function useDonateToGitcoin() {
  const context = useContext(DonateToGitcoinContext);
  if (!context) {
    throw new Error("useDonateToGitcoin must be used within a DonateToGitcoinProvider");
  }
  return context;
}